import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient, useIsFetching } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts'
import {
  AccountBalanceWallet as WalletIcon,
  ArrowForward as ArrowForwardIcon,
  AttachMoney as MoneyIcon,
  Campaign as CampaignIcon,
  DirectionsCar as FreighterIcon,
  DirectionsRun as MapsIcon,
  HealthAndSafety as HealthIcon,
  LocalShipping as ShipmentIcon,
  PlayArrow as PlayIcon,
  ReceiptLong as InvoiceIcon,
  Speed as SpeedIcon,
  Store as ShipperIcon,
  TrendingUp as TrendingUpIcon,
  VerifiedUser as KycIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  accountService,
  analyticsService,
  disputeService,
  financeService,
  kycService,
  notificationService,
  shipmentService,
  subscriptionService,
  systemHealthService,
} from '../api/adminServices'
import { cronJobService } from '../api/cronJobService'
import { shipmentStatusColors, shipperColor, freighterColor } from '../theme/theme'
import { formatCurrency } from '../utils/formatCurrency'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { useToast } from '../components/useToast'
import PageRefreshButton from '../components/PageRefreshButton'

// ---------- helpers ----------

function formatTimeAgo(dateStr?: string) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`
  const diffDays = Math.floor(diffHrs / 24)
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
}

function isToday(dateStr?: string) {
  if (!dateStr) return false
  const d = new Date(dateStr)
  const n = new Date()
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  )
}

interface StatCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  color: string
  onClick?: () => void
  loading?: boolean
}

function StatCard({ title, value, subtitle, icon, color, onClick, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="circular" width={48} height={48} />
          <Skeleton variant="text" width="60%" sx={{ mt: 2 }} />
          <Skeleton variant="text" width="40%" />
        </CardContent>
      </Card>
    )
  }
  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 24px ${alpha(color, 0.2)}`,
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.1),
              color,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

interface ActivityItem {
  type: 'shipment' | 'kyc' | 'dispute' | 'account' | 'invoice'
  title: string
  subtitle: string
  time: string
  timestamp: number
  status?: string
  onClick?: () => void
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const theme = useTheme()

  const iconFor = () => {
    switch (item.type) {
      case 'shipment':
        return <ShipmentIcon />
      case 'kyc':
        return <KycIcon />
      case 'dispute':
        return <WarningIcon />
      case 'account':
        return <ShipperIcon />
      case 'invoice':
        return <InvoiceIcon />
    }
  }
  const colorFor = () => {
    switch (item.type) {
      case 'shipment':
        return theme.palette.primary.main
      case 'kyc':
        return theme.palette.warning.main
      case 'dispute':
        return theme.palette.error.main
      case 'invoice':
        return theme.palette.info.main
      default:
        return theme.palette.success.main
    }
  }

  const statusConfig = item.status ? shipmentStatusColors[item.status] : null

  return (
    <ListItem
      sx={{
        px: 0,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
        cursor: item.onClick ? 'pointer' : 'default',
      }}
      onClick={item.onClick}
    >
      <ListItemAvatar>
        <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(colorFor(), 0.1), color: colorFor() }}>
          {iconFor()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              {item.title}
            </Typography>
            {statusConfig && (
              <Chip
                size="small"
                label={statusConfig.label}
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  bgcolor: statusConfig.bg,
                  color: statusConfig.text,
                }}
              />
            )}
          </Stack>
        }
        secondary={item.subtitle}
        primaryTypographyProps={{ noWrap: true }}
        secondaryTypographyProps={{ noWrap: true }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
        {item.time}
      </Typography>
    </ListItem>
  )
}

// ---------- main page ----------

export default function DashboardPage() {
  const navigate = useNavigate()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  const today = new Date()
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
  const revStart = monthAgo.toISOString().slice(0, 10)
  const revEnd = today.toISOString().slice(0, 10)

  // Quick-action feedback via shared toast
  const toast = useToast()
  const flash = (message: string, severity: 'success' | 'error' = 'success') =>
    severity === 'error' ? toast.error(message) : toast.success(message)

  // Broadcast dialog state
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [broadcastTitle, setBroadcastTitle] = useState('')
  const [broadcastBody, setBroadcastBody] = useState('')
  const [broadcastAudience, setBroadcastAudience] = useState('ALL')

  // ============ Queries ============
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts-dashboard'],
    queryFn: () => accountService.getAll(0, 1000),
  })

  const { data: subscriptionOverview } = useQuery({
    queryKey: ['subscription-overview'],
    queryFn: () => analyticsService.getSubscriptionOverview(),
  })

  const { data: accountStats } = useQuery({
    queryKey: ['account-stats-dashboard'],
    queryFn: () => accountService.getStatistics(),
  })

  const { data: revenueData } = useQuery({
    queryKey: ['dashboard-revenue', revStart, revEnd],
    queryFn: () => analyticsService.getRevenueAnalytics(revStart, revEnd),
  })

  const { data: pendingKyc } = useQuery({
    queryKey: ['pending-kyc'],
    queryFn: () => kycService.getPending(0, 100),
  })

  const { data: disputes } = useQuery({
    queryKey: ['disputes-dashboard'],
    queryFn: () => disputeService.getAll(0, 10),
  })

  const { data: shipmentStatsData, isLoading: shipmentStatsLoading } = useQuery({
    queryKey: ['shipment-statistics'],
    queryFn: () => shipmentService.getStatistics(),
  })

  const { data: recentShipmentsData } = useQuery({
    queryKey: ['recent-shipments'],
    queryFn: () => shipmentService.getRecent(10),
  })

  const { data: pendingPayments } = useQuery({
    queryKey: ['pending-payments-dashboard'],
    queryFn: () => financeService.getPendingPayments(),
  })

  const { data: overdueInvoices } = useQuery({
    queryKey: ['overdue-invoices-dashboard'],
    queryFn: () => subscriptionService.getOverdueInvoices(0, 10),
  })

  const { data: systemHealth } = useQuery({
    queryKey: ['system-health-dashboard'],
    queryFn: () => systemHealthService.getGatewayHealth(),
    refetchInterval: 60000,
    retry: false,
  })

  // ============ Mutations ============
  const runMatching = useMutation({
    mutationFn: () => cronJobService.triggerMatching(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-shipments'] })
      flash('Matching triggered')
    },
    onError: (e: any) =>
      flash(e?.response?.data?.message || 'Failed to trigger matching', 'error'),
  })

  const runMatchingToday = useMutation({
    mutationFn: () => cronJobService.triggerMatchingToday(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-shipments'] })
      flash("Today's matching triggered")
    },
    onError: (e: any) =>
      flash(e?.response?.data?.message || 'Failed to trigger matching', 'error'),
  })

  const broadcastMutation = useMutation({
    mutationFn: () => {
      const audienceToRoles: Record<string, any[] | undefined> = {
        ALL: undefined,
        SHIPPER: ['SHIPPER_DISTRIBUTION_MANAGER', 'SHIPPER_OBSERVER'],
        FREIGHTER: ['FREIGHTER_FOREMAN'],
        DRIVER: ['FREIGHTER_DRIVER'],
      }
      return notificationService.broadcast({
        channel: 'PUSH',
        title: broadcastTitle,
        message: broadcastBody,
        template: 'admin_broadcast',
        roles: audienceToRoles[broadcastAudience],
      })
    },
    onSuccess: (result: any) => {
      const sent = result?.sent ?? 0
      const recipients = result?.recipients ?? 0
      flash(`Broadcast sent to ${sent}/${recipients} recipient(s)`)
      setBroadcastOpen(false)
      setBroadcastTitle('')
      setBroadcastBody('')
      setBroadcastAudience('ALL')
    },
    onError: (e: any) =>
      flash(e?.response?.data?.message || 'Failed to broadcast', 'error'),
  })

  // ============ Derived ============
  const accounts = useMemo(
    () => (accountsData?.content || accountsData || []) as any[],
    [accountsData]
  )
  const freighterCount = accounts.filter(
    (a) => String(a.type || '').toUpperCase() === 'FREIGHTER'
  ).length
  const shipperCount = accounts.filter(
    (a) => String(a.type || '').toUpperCase() === 'SHIPPER'
  ).length
  const totalAccounts = freighterCount + shipperCount

  const pendingKycList = useMemo(
    () => (pendingKyc?.content || pendingKyc || []) as any[],
    [pendingKyc]
  )
  const pendingKycCount = pendingKycList.length

  const disputesList = useMemo(
    () => (disputes?.content || disputes || []) as any[],
    [disputes]
  )
  const openDisputesCount = disputesList.length

  const pendingPaymentsCount = Array.isArray(pendingPayments) ? pendingPayments.length : 0

  const overdueInvoicesList = useMemo(
    () => (overdueInvoices?.content || overdueInvoices || []) as any[],
    [overdueInvoices]
  )
  const overdueInvoicesCount = overdueInvoicesList.length

  const systemHealthStatus = systemHealth?.status || 'UNKNOWN'
  const isSystemUp = systemHealthStatus === 'UP'
  const isSystemDown = systemHealthStatus === 'DOWN'

  const recentShipments = useMemo(
    () => (recentShipmentsData || []) as any[],
    [recentShipmentsData]
  )
  const todayShipmentsCount = recentShipments.filter((s) =>
    isToday(s.createdAt)
  ).length

  // Shipment statistics
  const shipmentStats = {
    total: shipmentStatsData?.totalShipments || 0,
    pending: shipmentStatsData?.pendingShipments || 0,
    inTransit: shipmentStatsData?.inTransitShipments || 0,
    delivered: shipmentStatsData?.deliveredShipments || 0,
    completed: shipmentStatsData?.completedShipments || 0,
    cancelled: shipmentStatsData?.cancelledShipments || 0,
  }

  const pipelinePieData = [
    { name: 'Pending', value: shipmentStats.pending, color: theme.palette.warning.main },
    { name: 'In Transit', value: shipmentStats.inTransit, color: theme.palette.info.main },
    { name: 'Delivered', value: shipmentStats.delivered, color: theme.palette.success.main },
    {
      name: 'Completed',
      value: shipmentStats.completed,
      color: theme.palette.primary.main,
    },
    { name: 'Cancelled', value: shipmentStats.cancelled, color: theme.palette.error.main },
  ].filter((d) => d.value > 0)

  // Revenue chart: try to extract daily series from analytics response.
  // Backend shape is loose (`Map<String, Object>`); fall back to a single bar with `mrr`.
  const revenueSeries = useMemo(() => {
    const r: any = revenueData
    if (!r) return [] as { day: string; revenue: number }[]
    // Common shapes
    const series =
      r.dailyRevenue || r.daily || r.byDay || r.points || r.series || null
    if (Array.isArray(series)) {
      return series
        .map((p: any) => ({
          day: String(p.day ?? p.date ?? p.label ?? ''),
          revenue: Number(p.revenue ?? p.amount ?? p.value ?? 0),
        }))
        .filter((p) => p.day)
    }
    // Map of date -> amount
    if (typeof series === 'object' && series !== null) {
      return Object.entries(series as Record<string, any>)
        .map(([day, value]) => ({ day, revenue: Number(value) || 0 }))
        .sort((a, b) => a.day.localeCompare(b.day))
    }
    return []
  }, [revenueData])

  const revenueTotal = useMemo(
    () => revenueSeries.reduce((acc, p) => acc + (p.revenue || 0), 0),
    [revenueSeries]
  )

  // Combined activity feed (shipments + KYC + disputes + overdue invoices)
  const activity: ActivityItem[] = useMemo(() => {
    const items: ActivityItem[] = []

    recentShipments.slice(0, 8).forEach((s: any) => {
      const ts = new Date(s.updatedAt || s.createdAt || 0).getTime()
      const cargoSummary =
        s.cargos && s.cargos.length > 0
          ? s.cargos
              .map((c: any) => c.deliveryAddress)
              .filter(Boolean)
              .slice(0, 1)
              .join('')
          : ''
      items.push({
        type: 'shipment',
        title: `Shipment ${getLocalizedLabel('shipmentStatus', s.status) || s.status}`,
        subtitle: `${s.pickupAddress || s.id?.substring(0, 12) || ''}${cargoSummary ? ` → ${cargoSummary}` : ''}`,
        time: formatTimeAgo(s.updatedAt || s.createdAt),
        timestamp: ts,
        status: s.status,
        onClick: () => navigate('/shipments'),
      })
    })

    pendingKycList.slice(0, 5).forEach((v: any) => {
      const ts = new Date(v.createdAt || 0).getTime()
      items.push({
        type: 'kyc',
        title: `KYC pending — ${v.subjectType || 'subject'}`,
        subtitle: `Subject ${(v.subjectId || '').substring(0, 12)}…`,
        time: formatTimeAgo(v.createdAt),
        timestamp: ts,
        onClick: () => navigate('/pending-verifications'),
      })
    })

    disputesList.slice(0, 5).forEach((d: any) => {
      const ts = new Date(d.updatedAt || d.createdAt || 0).getTime()
      items.push({
        type: 'dispute',
        title: `Dispute · ${d.type || d.status || 'OPEN'}`,
        subtitle: d.id ? `${d.id.substring(0, 12)}…` : '',
        time: formatTimeAgo(d.updatedAt || d.createdAt),
        timestamp: ts,
        onClick: () => navigate('/disputes'),
      })
    })

    overdueInvoicesList.slice(0, 5).forEach((inv: any) => {
      const ts = new Date(inv.dueDate || inv.createdAt || 0).getTime()
      items.push({
        type: 'invoice',
        title: `Overdue invoice ${inv.invoiceNumber || inv.id?.substring(0, 8)}`,
        subtitle:
          inv.totalAmount != null
            ? `${formatCurrency(inv.totalAmount)} ${inv.currency || ''}`
            : '—',
        time: formatTimeAgo(inv.dueDate || inv.createdAt),
        timestamp: ts,
        onClick: () => navigate('/subscriptions'),
      })
    })

    return items.sort((a, b) => b.timestamp - a.timestamp).slice(0, 12)
  }, [
    recentShipments,
    pendingKycList,
    disputesList,
    overdueInvoicesList,
    navigate,
    getLocalizedLabel,
  ])

  const isLoading = accountsLoading || shipmentStatsLoading

  const isDashboardRefreshing = useIsFetching({
    predicate: (query) => {
      const key = query.queryKey[0]
      return (
        key === 'accounts-dashboard' ||
        key === 'account-stats-dashboard' ||
        key === 'dashboard-revenue' ||
        key === 'pending-kyc' ||
        key === 'disputes-dashboard' ||
        key === 'shipment-statistics' ||
        key === 'recent-shipments' ||
        key === 'pending-payments-dashboard' ||
        key === 'overdue-invoices-dashboard' ||
        key === 'system-health-dashboard'
      )
    },
  }) > 0

  const refreshDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['account-stats-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-revenue'] })
    queryClient.invalidateQueries({ queryKey: ['pending-kyc'] })
    queryClient.invalidateQueries({ queryKey: ['disputes-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['shipment-statistics'] })
    queryClient.invalidateQueries({ queryKey: ['recent-shipments'] })
    queryClient.invalidateQueries({ queryKey: ['pending-payments-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['overdue-invoices-dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['system-health-dashboard'] })
  }

  return (
    <Box className="animate-fade-in">
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <PageRefreshButton onRefresh={refreshDashboard} loading={isDashboardRefreshing} />
        <Chip
          icon={<HealthIcon />}
          label={
            systemHealth === undefined
              ? 'Checking system…'
              : isSystemUp
              ? 'System Operational'
              : isSystemDown
              ? 'System Issues Detected'
              : 'System Status Unknown'
          }
          color={isSystemUp ? 'success' : isSystemDown ? 'error' : 'default'}
          sx={{ fontWeight: 600, cursor: 'pointer' }}
          onClick={() => navigate('/system-health')}
        />
      </Box>

      {/* Primary KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Shipments"
            value={shipmentStats.total.toLocaleString()}
            subtitle="All time"
            icon={<ShipmentIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.primary.main}
            onClick={() => navigate('/shipments')}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Active Shipments"
            value={(shipmentStats.pending + shipmentStats.inTransit).toLocaleString()}
            subtitle={`${todayShipmentsCount} updated today`}
            icon={<SpeedIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.info.main}
            onClick={() => navigate('/shipments')}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Shipper Accounts"
            value={shipperCount}
            subtitle={`${totalAccounts} total accounts`}
            icon={<ShipperIcon sx={{ fontSize: 28 }} />}
            color={shipperColor}
            onClick={() => navigate('/accounts?type=SHIPPER')}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Freighter Accounts"
            value={freighterCount}
            subtitle={`${totalAccounts} total accounts`}
            icon={<FreighterIcon sx={{ fontSize: 28 }} />}
            color={freighterColor}
            onClick={() => navigate('/accounts?type=FREIGHTER')}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Pipeline donut + Revenue area chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" fontWeight={600}>
                  Shipment Pipeline
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {shipmentStats.total.toLocaleString()} total
                </Typography>
              </Stack>
              {pipelinePieData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Typography variant="body2" color="text.secondary">
                    No data yet
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pipelinePieData}
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {pipelinePieData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {pipelinePieData.map((d) => (
                  <Grid item xs={6} sm={4} key={d.name}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: d.color,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {d.name}: <strong>{d.value}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Subscription Revenue
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last 30 days · MRR{' '}
                    {subscriptionOverview?.mrr != null
                      ? formatCurrency(subscriptionOverview.mrr)
                      : '—'}
                  </Typography>
                </Box>
                {revenueTotal > 0 && (
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    {formatCurrency(revenueTotal)}
                  </Typography>
                )}
              </Stack>
              {revenueSeries.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Typography variant="body2" color="text.secondary">
                    No revenue series yet — backend hasn't reported daily breakdown.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <AreaChart data={revenueSeries}>
                      <defs>
                        <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.4}
                          />
                          <stop
                            offset="100%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis
                        dataKey="day"
                        stroke={theme.palette.text.secondary}
                        fontSize={11}
                      />
                      <YAxis stroke={theme.palette.text.secondary} fontSize={11} />
                      <RechartsTooltip />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={theme.palette.success.main}
                        strokeWidth={2}
                        fill="url(#revFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Platform Snapshot */}
      {accountStats && (
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                Platform Snapshot
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Accounts &amp; users
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {[
                {
                  label: 'Total accounts',
                  value:
                    (accountStats as any).totalAccountsCount ??
                    (accountStats as any).totalAccounts,
                },
                {
                  label: 'Active',
                  value:
                    (accountStats as any).activeAccountsCount ??
                    (accountStats as any).activeAccounts,
                  color: 'success.main',
                },
                {
                  label: 'Suspended',
                  value: (accountStats as any).suspendedAccountsCount,
                  color: 'error.main',
                },
                {
                  label: 'Avg rating',
                  value:
                    (accountStats as any).averageAccountRating != null
                      ? Number((accountStats as any).averageAccountRating).toFixed(2)
                      : null,
                },
                {
                  label: 'New accounts (30d)',
                  value: (accountStats as any).accountsCreatedLast30Days,
                },
                {
                  label: 'New users (30d)',
                  value: (accountStats as any).usersCreatedLast30Days,
                },
                {
                  label: 'Active users (30d)',
                  value: (accountStats as any).usersLoginLast30Days,
                },
                {
                  label: 'Avg users / account',
                  value:
                    (accountStats as any).averageUsersPerAccount != null
                      ? Number((accountStats as any).averageUsersPerAccount).toFixed(1)
                      : null,
                },
              ].map((m) => (
                <Grid item xs={6} sm={3} md={3} lg={1.5 as any} key={m.label}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {m.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: (m as any).color || 'text.primary' }}
                    >
                      {m.value != null && m.value !== '' ? m.value : '—'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Requires Attention */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Requires Attention
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click any tile to jump to its workspace
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                {[
                  {
                    label: 'Pending KYC',
                    value: pendingKycCount,
                    color: theme.palette.warning.main,
                    icon: <KycIcon />,
                    onClick: () => navigate('/pending-verifications'),
                  },
                  {
                    label: 'Open Disputes',
                    value: openDisputesCount,
                    color: theme.palette.error.main,
                    icon: <WarningIcon />,
                    onClick: () => navigate('/disputes'),
                  },
                  {
                    label: 'Pending Payments',
                    value: pendingPaymentsCount,
                    color: theme.palette.info.main,
                    icon: <MoneyIcon />,
                    onClick: () => navigate('/finance'),
                  },
                  {
                    label: 'Overdue Invoices',
                    value: overdueInvoicesCount,
                    color: theme.palette.secondary.main,
                    icon: <InvoiceIcon />,
                    onClick: () => navigate('/subscriptions'),
                  },
                  {
                    label: 'Active Subscriptions',
                    value: subscriptionOverview?.activeSubscriptions || 0,
                    color: theme.palette.success.main,
                    icon: <WalletIcon />,
                    onClick: () => navigate('/subscriptions'),
                  },
                ].map((tile) => (
                  <Grid item xs={6} sm={4} md={2.4} key={tile.label}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: alpha(tile.color, 0.08),
                        border: `1px solid ${alpha(tile.color, 0.2)}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: alpha(tile.color, 0.15),
                        },
                      }}
                      onClick={tile.onClick}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ color: tile.color, display: 'flex' }}>{tile.icon}</Box>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{ color: tile.color }}
                        >
                          {tile.value}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {tile.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Activity + Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" fontWeight={600}>
                  Recent Activity
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Shipments · KYC · disputes · overdue invoices
                </Typography>
              </Stack>
              {activity.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Typography variant="body2" color="text.secondary">
                    Nothing has happened recently.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {activity.map((item, i) => (
                    <ActivityRow key={`${item.type}-${i}`} item={item} />
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  startIcon={<PlayIcon />}
                  onClick={() => runMatching.mutate()}
                  disabled={runMatching.isPending}
                >
                  {runMatching.isPending ? 'Triggering…' : 'Run Matching'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  startIcon={<PlayIcon />}
                  onClick={() => runMatchingToday.mutate()}
                  disabled={runMatchingToday.isPending}
                >
                  Match Today
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  color="secondary"
                  startIcon={<CampaignIcon />}
                  onClick={() => setBroadcastOpen(true)}
                >
                  Broadcast Notification
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Go to…
              </Typography>
              <Stack spacing={1}>
                {[
                  {
                    label: 'Review KYC',
                    icon: <KycIcon />,
                    path: '/pending-verifications',
                    color: theme.palette.warning.main,
                  },
                  {
                    label: 'Disputes',
                    icon: <WarningIcon />,
                    path: '/disputes',
                    color: theme.palette.error.main,
                  },
                  {
                    label: 'Finance',
                    icon: <MoneyIcon />,
                    path: '/finance',
                    color: theme.palette.info.main,
                  },
                  {
                    label: 'Live Tracking',
                    icon: <MapsIcon />,
                    path: '/maps',
                    color: '#8b5cf6',
                  },
                  {
                    label: 'System Health',
                    icon: <HealthIcon />,
                    path: '/system-health',
                    color: isSystemDown
                      ? theme.palette.error.main
                      : theme.palette.success.main,
                  },
                ].map((action) => (
                  <Paper
                    key={action.path}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        bgcolor: alpha(action.color, 0.05),
                        borderColor: alpha(action.color, 0.3),
                      },
                    }}
                    onClick={() => navigate(action.path)}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: alpha(action.color, 0.1),
                        color: action.color,
                      }}
                    >
                      {action.icon}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                      {action.label}
                    </Typography>
                    <ArrowForwardIcon
                      sx={{ ml: 'auto', fontSize: 18, color: 'text.secondary' }}
                    />
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Broadcast dialog */}
      <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Broadcast Notification</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Sends an in-app notification to the selected audience.
            </Alert>
            <TextField
              select
              label="Audience"
              value={broadcastAudience}
              onChange={(e) => setBroadcastAudience(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="ALL">All users</MenuItem>
              <MenuItem value="SHIPPER">Shipper users</MenuItem>
              <MenuItem value="FREIGHTER">Freighter users</MenuItem>
              <MenuItem value="DRIVER">Drivers</MenuItem>
            </TextField>
            <TextField
              label="Title *"
              value={broadcastTitle}
              onChange={(e) => setBroadcastTitle(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Message *"
              value={broadcastBody}
              onChange={(e) => setBroadcastBody(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CampaignIcon />}
            disabled={
              !broadcastTitle.trim() || !broadcastBody.trim() || broadcastMutation.isPending
            }
            onClick={() => broadcastMutation.mutate()}
          >
            {broadcastMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Suppress unused-imports check — TrendingUpIcon is reserved for upcoming "vs last week" badge.
void TrendingUpIcon
